import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAuth } from "../context/ApiKeyContext";
import { useTranslation } from "../hooks/useTranslation";
import { getApiBaseUrl } from "../config";
import ConsumptionFilters, {
  PALETTE,
} from "../components/consumption/ConsumptionFilters";
import GroupBySelect from "../components/consumption/GroupBySelect";
import MetricPanel from "../components/consumption/MetricPanel";
import ConsumptionDetailsTable from "../components/consumption/ConsumptionDetailsTable";
import ExportButtons from "../components/consumption/ExportButtons";
import {
  defaultCycle,
  isoDateToBR,
  monthRangeISO,
  toISO,
  fromISO,
} from "../components/consumption/dateUtils";
import "./ConsumptionReport.css";

const API_BASE_URL = getApiBaseUrl();
const SERVICE_NAME = "CONSUMPTION_REPORT";

/* Tela "Relatórios de Consumo" — fiel ao mockup aprovado em
   docs/mockups/consumo-relatorio.html. Backend: /v1/consumption_report/*.

   Fluxo: mount → filter_options (papel, combos, ciclo default, show_cost);
   Aplicar → summary + details em paralelo; trocar "Agrupar por" → só summary;
   paginação/ordenação → só details; clique numa barra (visão dia) → drill-down
   hora a hora com Voltar (restaura o summary diário cacheado, sem refetch). */
const buildDraftFromCycle = (cycle) => {
  const today = new Date();
  return {
    periodMode: "range",
    dateStart: cycle.start,
    dateEnd: cycle.end,
    dateStartText: isoDateToBR(cycle.start),
    dateEndText: isoDateToBR(cycle.end),
    refMonth: today.getMonth(),
    refYear: today.getFullYear(),
    refDay: toISO(today),
    refDayText: isoDateToBR(toISO(today)),
    productCodes: null,
    companyIds: null,
    tenantIds: null,
    cellIds: null,
    userIds: null,
    includeIntegration: true,
    jobName: "",
  };
};

/* Converte o draft em filtros efetivos (datas resolvidas por modo). */
const draftToApplied = (draft) => {
  if (draft.periodMode === "month") {
    const range = monthRangeISO(draft.refYear, draft.refMonth);
    return { ...draft, dateStart: range.start, dateEnd: range.end, granularity: "day" };
  }
  if (draft.periodMode === "day") {
    return { ...draft, dateStart: draft.refDay, dateEnd: draft.refDay, granularity: "hour" };
  }
  return { ...draft, granularity: "day" };
};

const ConsumptionReportPage = () => {
  const { token } = useAuth();
  const { t } = useTranslation("consumption");

  const [options, setOptions] = useState(null);
  const [optionsError, setOptionsError] = useState(null);
  const [draft, setDraft] = useState(() => buildDraftFromCycle(defaultCycle()));
  const [applied, setApplied] = useState(null);
  const [groupBy, setGroupBy] = useState("product");
  const [drillDate, setDrillDate] = useState(null);
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [details, setDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [panelView, setPanelView] = useState({ jobs: "chart", pages: "chart", cost: "chart" });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortBy, setSortBy] = useState("insertion_timestamp");
  const [sortOrder, setSortOrder] = useState("desc");
  const [exporting, setExporting] = useState(null);
  /* Erros de summary/details exibidos em banner — sem isso o usuário vê
     painéis zerados sem saber que a API recusou a consulta (ex.: 422).
     Slots separados: o sucesso de um fetch não pode apagar o erro do outro. */
  const [apiErrors, setApiErrors] = useState({ summary: null, details: null });
  const setApiError = (slot, msg) =>
    setApiErrors((prev) => ({ ...prev, [slot]: msg }));
  const apiError = apiErrors.summary || apiErrors.details;

  const daySummaryCache = useRef({ key: null, data: null });

  const role = options?.role || "company_admin";
  const showCost = Boolean(options?.show_cost);

  const headers = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
      ServiceName: SERVICE_NAME,
      accept: "application/json",
    }),
    [token]
  );

  const buildParams = useCallback((appliedFilters, extra = {}) => {
    const params = new URLSearchParams({
      date_start: appliedFilters.dateStart,
      date_end: appliedFilters.dateEnd,
      include_integration: String(appliedFilters.includeIntegration),
      ...extra,
    });
    if (appliedFilters.productCodes)
      params.set("product_codes", appliedFilters.productCodes.join(","));
    if (appliedFilters.companyIds)
      params.set("company_ids", appliedFilters.companyIds.join(","));
    if (appliedFilters.tenantIds)
      params.set("tenant_ids", appliedFilters.tenantIds.join(","));
    if (appliedFilters.cellIds)
      params.set("cell_ids", appliedFilters.cellIds.join(","));
    if (appliedFilters.userIds)
      params.set("user_ids", appliedFilters.userIds.join(","));
    if (appliedFilters.jobName) params.set("job_name", appliedFilters.jobName);
    return params;
  }, []);

  /* ---------------- filter_options (mount) ---------------- */
  useEffect(() => {
    if (!token) return;
    fetch(`${API_BASE_URL}/consumption_report/filter_options`, { headers })
      .then(async (res) => {
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.detail || `HTTP ${res.status}`);
        }
        return res.json();
      })
      .then((data) => {
        setOptions(data);
        const cycle = data.default_cycle || defaultCycle();
        const initialDraft = buildDraftFromCycle(cycle);
        setDraft(initialDraft);
        setApplied(draftToApplied(initialDraft));
      })
      .catch((err) => {
        console.error("Erro ao carregar opções do relatório:", err);
        setOptionsError(err.message);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  /* ---------------- summary ---------------- */
  useEffect(() => {
    if (!applied) return;
    const isDrill = drillDate !== null;
    const granularity = isDrill ? "hour" : applied.granularity;
    const dateStart = isDrill ? drillDate : applied.dateStart;
    const dateEnd = isDrill ? drillDate : applied.dateEnd;

    // Voltar do drill-down: restaura visão diária do cache sem refetch
    const cacheKey = JSON.stringify([applied, groupBy]);
    if (!isDrill && applied.granularity === "day") {
      if (daySummaryCache.current.key === cacheKey && daySummaryCache.current.data) {
        setSummary(daySummaryCache.current.data);
        return;
      }
    }

    const controller = new AbortController();
    setSummaryLoading(true);
    const params = buildParams(
      { ...applied, dateStart, dateEnd },
      { granularity, group_by: groupBy }
    );
    fetch(`${API_BASE_URL}/consumption_report/summary?${params}`, {
      headers,
      signal: controller.signal,
    })
      .then(async (res) => {
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.detail || `HTTP ${res.status}`);
        }
        return res.json();
      })
      .then((data) => {
        setSummary(data);
        setApiError("summary", null);
        if (!isDrill && applied.granularity === "day") {
          daySummaryCache.current = { key: cacheKey, data };
        }
      })
      .catch((err) => {
        if (err.name !== "AbortError") {
          console.error("Erro ao carregar resumo de consumo:", err);
          setSummary(null);
          setApiError("summary", err.message);
        }
      })
      .finally(() => setSummaryLoading(false));
    return () => controller.abort();
  }, [applied, groupBy, drillDate, headers, buildParams]);

  /* ---------------- details ---------------- */
  useEffect(() => {
    if (!applied) return;
    const controller = new AbortController();
    setDetailsLoading(true);
    const params = buildParams(applied, {
      page: String(page),
      page_size: String(pageSize),
      sort_by: sortBy,
      sort_order: sortOrder,
    });
    fetch(`${API_BASE_URL}/consumption_report/details?${params}`, {
      headers,
      signal: controller.signal,
    })
      .then(async (res) => {
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.detail || `HTTP ${res.status}`);
        }
        return res.json();
      })
      .then((data) => {
        setDetails(data);
        setApiError("details", null);
      })
      .catch((err) => {
        if (err.name !== "AbortError") {
          console.error("Erro ao carregar extrato de consumo:", err);
          setDetails(null);
          setApiError("details", err.message);
        }
      })
      .finally(() => setDetailsLoading(false));
    return () => controller.abort();
  }, [applied, page, pageSize, sortBy, sortOrder, headers, buildParams]);

  /* ---------------- handlers ---------------- */
  const handleApply = () => {
    daySummaryCache.current = { key: null, data: null };
    setDrillDate(null);
    setPage(1);
    setApplied(draftToApplied(draft));
  };

  const handleClear = () => {
    const cycle = options?.default_cycle || defaultCycle();
    const cleanDraft = buildDraftFromCycle(cycle);
    setDraft(cleanDraft);
    daySummaryCache.current = { key: null, data: null };
    setDrillDate(null);
    setPage(1);
    setApplied(draftToApplied(cleanDraft));
  };

  const handleSort = (key) => {
    if (sortBy === key) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(key);
      setSortOrder("asc");
    }
    setPage(1);
  };

  const handleExport = async (format) => {
    if (!applied) return;
    setExporting(format);
    try {
      const params = buildParams(applied);
      const res = await fetch(
        `${API_BASE_URL}/consumption_report/export/${format}?${params}`,
        { headers }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `relatorio_consumo_${applied.dateStart}_${applied.dateEnd}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(`Erro ao exportar ${format}:`, err);
      window.alert(t("exportError", { error: err.message }));
    } finally {
      setExporting(null);
    }
  };

  /* ---------------- derivados de render ---------------- */
  const groupByOptions = useMemo(() => {
    const available = options?.available_group_bys || [
      "product", "service", "tenant", "cell", "user", "integration",
    ];
    return available.map((key) => ({ key, label: t(`dims.${key}`) }));
  }, [options, t]);

  const groupsWithColor = useMemo(
    () =>
      (summary?.groups || []).map((g, i) => ({
        ...g,
        color:
          g.key === "__others__"
            ? "var(--ikaro-gray-medium)"
            : PALETTE[i % PALETTE.length],
      })),
    [summary]
  );

  const colorByKey = useMemo(() => {
    const map = {};
    groupsWithColor.forEach((g) => {
      map[g.key] = g.color;
    });
    return map;
  }, [groupsWithColor]);

  const fmtInt = (v) => Number(v || 0).toLocaleString("pt-BR");
  const fmtMoney = (v) =>
    `R$ ${Number(v || 0).toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const metrics = useMemo(() => {
    const base = [
      { key: "jobs", label: t("processings"), color: "#D84E2B", formatter: fmtInt },
      { key: "pages", label: t("pages"), color: "#643184", formatter: fmtInt },
    ];
    if (showCost) {
      base.push({ key: "cost", label: t("cost"), color: "#16a34a", formatter: fmtMoney });
    }
    return base;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCost, t]);

  const isHourView =
    drillDate !== null || (applied && applied.granularity === "hour");
  const caption = isHourView ? t("totalInDay") : t("totalInPeriod");
  const dimensionLabel = t(`dims.${groupBy}`);

  const drillLabel = useMemo(() => {
    if (!drillDate) return "";
    const d = fromISO(drillDate);
    const dow = d.toLocaleDateString("pt-BR", { weekday: "short" });
    return `${dow}, ${isoDateToBR(drillDate)}`;
  }, [drillDate]);

  const cycleHint = options?.default_cycle
    ? t("currentCycle", {
        start: isoDateToBR(options.default_cycle.start),
        end: isoDateToBR(options.default_cycle.end),
      })
    : "";

  /* ---------------- render ---------------- */
  if (optionsError) {
    return (
      <div className="consumption-report">
        <div className="page-head">
          <div>
            <h1>{t("title")}</h1>
            <p className="sub">{t("accessDenied", { error: optionsError })}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="consumption-report">
      <div className="page-head">
        <div>
          <h1>{t("title")}</h1>
          <p className="sub">{t("subtitle")}</p>
        </div>
        <ExportButtons onExport={handleExport} exporting={exporting} />
      </div>

      <ConsumptionFilters
        draft={draft}
        setDraft={setDraft}
        options={options}
        role={role}
        onApply={handleApply}
        onClear={handleClear}
        loading={summaryLoading || detailsLoading}
        cycleHint={cycleHint}
      />

      <div className="metrics-toolbar">
        <div className="crumb">
          {drillDate ? (
            <>
              <button
                type="button"
                className="back"
                onClick={() => setDrillDate(null)}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M19 12H5M12 19l-7-7 7-7" />
                </svg>
                {t("back")}
              </button>
              <span className="muted">{t("hourlyDetail")}</span>
              <span className="day">{drillLabel}</span>
            </>
          ) : (
            <span className="muted">
              {applied
                ? isHourView
                  ? t("hourlyView", { date: isoDateToBR(applied.dateStart) })
                  : t("dailyView", {
                      start: isoDateToBR(applied.dateStart),
                      end: isoDateToBR(applied.dateEnd),
                    })
                : ""}
            </span>
          )}
        </div>
        <div className="groupby">
          <span className="lbl">{t("groupBy")}</span>
          <GroupBySelect
            options={groupByOptions}
            value={groupBy}
            onChange={(v) => setGroupBy(v)}
          />
        </div>
      </div>

      {apiError && (
        <div className="api-error-banner" role="alert">
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <path d="M12 8v4M12 16h.01" />
          </svg>
          <span>{t("queryError", { error: apiError })}</span>
        </div>
      )}

      <div className="panels">
        {metrics.map((metric) => (
          <MetricPanel
            key={metric.key}
            metricKey={metric.key}
            label={metric.label}
            dotColor={metric.color}
            summary={summary}
            colorByKey={colorByKey}
            groupsWithColor={groupsWithColor}
            dimensionLabel={dimensionLabel}
            formatter={metric.formatter}
            caption={caption}
            view={panelView[metric.key]}
            onToggleView={(v) =>
              setPanelView((prev) => ({ ...prev, [metric.key]: v }))
            }
            onBarClick={
              !isHourView ? (bucket) => setDrillDate(bucket) : null
            }
            loading={summaryLoading}
          />
        ))}
      </div>

      <ConsumptionDetailsTable
        rows={details?.rows || []}
        pagination={details?.pagination}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={handleSort}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
        showCost={showCost}
        loading={detailsLoading}
      />
    </div>
  );
};

export default ConsumptionReportPage;
